import * as React from 'react';
import type { CallSortBy } from '@relay-towing/api';
import {
  Button,
  CallCard,
  DetailField,
  Divider,
  EmptyState,
  firstLine,
  Loading,
  locality,
  MobileHeader,
  MobileScreen,
  Stack,
  StatPair,
  Text,
  weightClassOf,
} from '@relay-towing/ui';
import { EnvelopeSimple, PhoneCall, Trophy } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDriver } from '../../hooks/useDriver';
import { useDriverPerformance } from '../../hooks/useDriverPerformance';
import { usePagedList } from '../../hooks/usePagedList';
import { openExternal } from '../../lib/nativeBridge';
import type { CallRecord } from '../calls/callRecord';
import { periodSince } from '../period';
import { PagedRows } from '../shell/PagedRows';
import { useBack } from '../shell/useBack';
import { dutyLabel, dutyTone, minutesLabel, truckLabel } from '../status';

/**
 * One driver, opened from Drivers. No Figma frame exists for it: the
 * layout is inferred, and a designer's frame replaces it. It is laid out as a call with no map is (Figma 344:65042):
 * their name over their truck, then their details with a button to ring
 * them, their last seven days as the old performance screen counted them
 * (DriverPerformanceScreen), and their calls today, which the server
 * answers (`drivers`, `view=today`), so a call still under way from
 * yesterday is among them.
 */
export function DriverDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBack('/drivers');
  const navigate = useNavigate();
  const { data: driver, isLoading } = useDriver(id);
  const range = React.useMemo(() => periodSince('week'), []);
  const performance = useDriverPerformance(id ?? '', range);
  const calls = usePagedList<CallRecord>(
    'mobile-driver-calls',
    '/calls',
    {
      drivers: id,
      view: 'today',
      sortBy: 'board' satisfies CallSortBy,
    },
    { enabled: !!id }
  );

  if (isLoading) return <Loading label="Loading the driver" fill="screen" />;
  if (!driver)
    return (
      <MobileScreen
        header={<MobileHeader onBack={back} backLabel="Back to drivers" />}
      >
        <EmptyState
          title="This driver is not available"
          description="They may have left the company, or the connection dropped."
        />
      </MobileScreen>
    );

  const phone = driver.phone ? String(driver.phone) : null;

  return (
    <MobileScreen
      header={
        <MobileHeader
          onBack={back}
          backLabel="Back to drivers"
          status={{
            label: dutyLabel(driver.status),
            tone: dutyTone(driver.status),
          }}
        />
      }
    >
      <Stack gap={6}>
        <Stack gap={1}>
          <Text as="h1" variant="h1">
            {driver.name}
          </Text>
          <Text tone="muted">{truckLabel(driver.truckNumber)}</Text>
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Details
          </Text>
          <DetailField
            label="Phone"
            value={phone ?? undefined}
            action={
              phone ? (
                <Button
                  variant="secondary"
                  size="icon-lg"
                  pill
                  aria-label={`Call ${driver.name}`}
                  onClick={() => openExternal(`tel:${phone}`)}
                >
                  <PhoneCall />
                </Button>
              ) : undefined
            }
          />
          <DetailField
            label="Email"
            value={driver.email ?? undefined}
            action={
              driver.email ? (
                <Button
                  variant="secondary"
                  size="icon-lg"
                  pill
                  aria-label={`Email ${driver.name}`}
                  onClick={() => openExternal(`mailto:${driver.email}`)}
                >
                  <EnvelopeSimple />
                </Button>
              ) : undefined
            }
          />
        </Stack>
        <Divider />
        <StatPair
          title="Last 7 days"
          icon={Trophy}
          stats={[
            {
              label: 'Completed calls',
              value: performance.data?.totalCompletedCalls ?? '—',
              loading: performance.isLoading,
            },
            {
              label: 'Average response',
              value: minutesLabel(performance.data?.averageResponseTime),
              loading: performance.isLoading,
            },
          ]}
        />
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Calls today
          </Text>
          <PagedRows list={calls} empty="No calls today">
            {(call) => (
              <CallCard
                platform="phone"
                key={call.id}
                address={firstLine(call.pickupLocation)}
                locality={locality(call.pickupLocation)}
                status={call.status}
                priority={call.priority}
                number={call.dispatchNumber}
                weightClass={weightClassOf(call.towType)}
                onClick={() => navigate(`/calls/${call.id}`)}
              />
            )}
          </PagedRows>
        </Stack>
      </Stack>
    </MobileScreen>
  );
}
